import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  GridItem,
  Text,
  Spinner,
  Input,
  VStack,
  HStack,
  useToast,
  Flex,
  Heading,
  useColorModeValue
} from '@chakra-ui/react';
import api from '../api';

const ProductCardPrinter = () => {
  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const filterBg = useColorModeValue('white', 'gray.800');
  const priceBg = useColorModeValue('gray.100', 'gray.700');
  
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const toast = useToast();
  const observer = useRef();

  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/products/?page=${page}&search=${search}`);
        const results = response.data.results || [];
        setProducts((prev) => [...prev, ...results]);
        setHasMore(Boolean(response.data.next));
      } catch (error) {
        toast({ title: 'Помилка при завантаженні товарів', status: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [page, search]);

  const handleToggleProduct = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const handlePrint = () => {
    const toPrint = products.filter((product) => selectedProducts.includes(product.id));
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
      <html>
      <head>
        <title>Картки товарів</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            margin: 0;
            padding: 20px;
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            align-items: flex-start;
          }
          .card {
            border: 2px solid #000;
            border-radius: 8px;
            width: 220px;
            height: 140px;
            margin: 10px;
            padding: 10px;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            position: relative;
            page-break-inside: avoid;
          }
          .header {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            font-weight: bold;
          }
          .name {
            text-align: center;
            font-size: 15px;
            font-weight: bold;
            margin: 6px 0;
            overflow-wrap: break-word;
            white-space: normal;
            max-height: 48px;
            overflow: hidden;
          }
          .catalog {
            text-align: center;
            font-size: 11px;
            color: #333;
          }
          .price {
            text-align: center;
            font-size: 20px;
            font-weight: bold;
            margin-top: 4px;
          }
        </style>
      </head>
      <body>
    `);

    toPrint.forEach((product) => {
      printWindow.document.write(`
        <div class="card">
          <div class="header">
            <span>ID: ${product.id}</span>
            <span></span>
          </div>
          <div class="name">${product.name}</div>
          <div class="catalog">Арт: ${product.catalog_number || '—'}</div>
          <div class="price">Ціна: ${Number(product.price).toFixed(2)} грн</div>
        </div>
      `);
    });

    printWindow.document.write(`
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Box position="relative" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      <Box position="relative" zIndex={1} p={8}>
        {/* Header Section - Modern Card */}
        <Box
          bg={cardBg}
          borderRadius="2xl"
          border="1px solid"
          borderColor={borderColor}
          p={8}
          mb={8}
          boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
          position="relative"
          overflow="hidden"
        >
          {/* Gradient accent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="6px"
            bg="linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c)"
          />
          
          <Heading 
            size="xl"
            bgGradient="linear(to-r, #667eea, #764ba2)"
            bgClip="text"
            fontWeight="bold"
          >
            🖨️ Друк карток товарів
          </Heading>
        </Box>

        {/* Filters Section - Modern Card */}
        <Box 
          bg={filterBg}
          borderRadius="xl"
          border="1px solid"
          borderColor={borderColor}
          p={6}
          mb={8}
          boxShadow={`0 10px 15px -3px ${shadowColor}`}
          position="relative"
          overflow="hidden"
        >
          {/* Gradient accent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bg="linear-gradient(90deg, #4facfe, #00f2fe)"
          />
          
          <HStack spacing={4} w="full">
            <Input
              placeholder="🔍 Пошук за назвою, ID або артикулом..."
              value={search}
              onChange={(e) => {
                setProducts([]);
                setPage(1);
                setSearch(e.target.value);
              }}
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="lg"
              size="lg"
              _focus={{
                borderColor: "#4facfe",
                boxShadow: "0 0 0 1px rgba(79, 172, 254, 0.3)",
              }}
              _placeholder={{
                color: subtleTextColor,
              }}
            />
            <Button 
              onClick={handlePrint} 
              isDisabled={selectedProducts.length === 0}
              bg="linear-gradient(135deg, #667eea, #764ba2)"
              color="white"
              size="lg"
              borderRadius="xl"
              px={8}
              boxShadow="0 4px 15px rgba(102, 126, 234, 0.4)"
              _hover={{
                bg: "linear-gradient(135deg, #5a6fd8, #6a4190)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 25px rgba(102, 126, 234, 0.6)",
              }}
              _active={{
                transform: "translateY(0)",
              }}
              _disabled={{
                bg: "gray.300",
                color: "gray.500",
                transform: "none",
                boxShadow: "none",
              }}
            >
              🖨️ Друкувати обране ({selectedProducts.length})
            </Button>
          </HStack>
        </Box>

          {/* Products Grid - Modern Card Container */}

          <Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={6} mt={4}>
            {products.map((product, index) => {
              const isLast = index === products.length - 1;
              return (
                <GridItem
                  key={product.id}
                  ref={isLast ? lastProductRef : null}
                  bg={cardBg}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor={borderColor}
                  p={4}
                  boxShadow={`0 10px 15px -3px ${shadowColor}`}
                  position="relative"
                  overflow="hidden"
                  transition="all 0.3s ease"
                  _hover={{ 
                    transform: "translateY(-4px)",
                    boxShadow: `0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`,
                  }}
                >
                  {/* Gradient accent */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    height="4px"
                    bg="linear-gradient(90deg, #667eea, #764ba2)"
                  />
                  
                  <VStack align="stretch" h="100%" justify="space-between" spacing={3} pt={2}>
                    {/* Top row: ID and checkbox */}
                    <Flex justify="space-between" align="flex-start">
                      <Text fontSize="xs" color={subtleTextColor} fontWeight="semibold">
                        📦 ID: {product.id}
                      </Text>
                      <Checkbox
                        isChecked={selectedProducts.includes(product.id)}
                        onChange={() => handleToggleProduct(product.id)}
                        colorScheme="blue"
                        size="lg"
                      />
                    </Flex>

                    {/* Catalog Number */}
                    <Text fontSize="xs" color={subtleTextColor} noOfLines={1}>
                      🏷️ Артикул: {product.catalog_number || '—'}
                    </Text>

                    {/* Product name */}
                    <Box flex="1" minH="60px">
                      <Text
                        fontWeight="bold"
                        fontSize="md"
                        color={textColor}
                        wordBreak="break-word"
                        whiteSpace="normal"
                        overflowWrap="break-word"
                      >
                        {product.name}
                      </Text>
                    </Box>

                    {/* Price */}
                    <Box 
                      textAlign="center" 
                      mt="auto"
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      p={3}
                      bg={priceBg}
                    >
                      <Text fontSize="md" fontWeight="bold" color={textColor}>
                        💰 Ціна: {Number(product.price).toFixed(2)} грн
                      </Text>
                    </Box>
                  </VStack>
                </GridItem>
              );
            })}
          </Grid>

          {loading && (
            <Box textAlign="center" mt={6}>
              <Spinner 
                size="xl" 
                color="blue.500"
                thickness="4px"
              />
              <Text mt={2} color={subtleTextColor}>
                Завантаження товарів...
              </Text>
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default ProductCardPrinter;